/** @jsxImportSource @emotion/react */
import { Drawer } from "../../components/Drawer";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as authSelectors from "../../module/auth/authSelectors";
import { AutoAddedProductMutationPayload } from "../../service/api-response/AutoAddedProducts";
import { ButtonAction } from "../../components/NewButtons";
import { useCreateAutoAddedProduct } from "../../service/api";
import { getErrorMessage } from "../../error-handler";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { ActionSpinner } from "../../components/ActionSpinner";
import { SelectProductInput } from "../../components/forms/inputs/SelectProductInput";
import { css } from "@emotion/react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const bodyStyling = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

type FormValues = AutoAddedProductMutationPayload;

const schema = yup.object().shape({
  productId: yup.string().required("Product is required"),
});

export const AddAutoAddedProductDrawer = ({ isOpen, onClose }: Props) => {
  const { shopId } = useParams();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const form = useForm<FormValues>({
    defaultValues: {
      amount: 1,
      locked: true,
      productId: "",
      triggerProductId: null,
    },
    resolver: yupResolver(schema),
  });
  const {
    mutateAsync: createAutoAddedProduct,
    isLoading: createAutoAddedProductLoading,
  } = useCreateAutoAddedProduct();

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (data: FormValues) => {
    if (!shopId) {
      return;
    }

    try {
      await createAutoAddedProduct({
        data: {
          ...data,
          shopId,
        },
        organizationKeyName,
      });

      toastr.success("Success", "Products added successfully");
      handleClose();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  return (
    <Drawer
      open={isOpen}
      onClose={handleClose}
      title="Add auto added product"
      width={500}
      buttons={
        <ButtonAction
          disabled={createAutoAddedProductLoading}
          onClick={form.handleSubmit(handleSubmit)}
        >
          {createAutoAddedProductLoading ? (
            <ActionSpinner />
          ) : (
            "Add auto added product"
          )}
        </ButtonAction>
      }
    >
      <FormProvider {...form}>
        <form css={bodyStyling}>
          <SelectProductInput
            name="productId"
            classification="generic"
            label="Product to add automatically to customer order"
          />
        </form>
      </FormProvider>
    </Drawer>
  );
};
